// Inside the theatre: the identity check, the anaesthesia record, orders, what
// was used, the count, referrals, the WHO Time-Out and Sign-Out, and check-out.
//
// Two hard gates are visible here, and both are the server's:
//
//   ENTERING is refused until the identity check is complete (OT_0042).
//   LEAVING is refused on an unresolved count discrepancy, and without the
//   anaesthetist's fitness certification (OT_0062, OT_0066).
//
// The count is the safety net that stops something being left inside a patient.
// Its result is COMPUTED from the numbers — this screen never lets anyone
// declare a count correct.

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";

import {
  IPD_COLORS,
  IPD_SHAPE,
  IpdButton,
  IpdFormField,
  IpdSectionCard,
  IpdTabBar,
  IpdTextField,
} from "../../../ipd/utils";
import { formatIstDateTime, formatIstTime } from "../../../../utils/istDateTime";
import {
  addAnaesthesiaDrug,
  addAnaesthesiaMonitor,
  enterTheatre,
  fetchAnaesthesiaRecord,
  fetchConsumption,
  fetchIdentityCheck,
  fetchReferrals,
  fetchSwabCounts,
  fetchWhoChecklist,
  otCheckout,
  raiseReferral,
  recordConsumption,
  saveAnaesthesiaRecord,
  saveIdentityCheck,
  correctWheelTimes,
  itemPriceOf,
  recordCountJuncture,
  searchInventoryItems,
  startSwabCount,
  updateSwabCount,
  verifySwabCount,
} from "../../api/otApi";
import {
  CheckList,
  CountResultChip,
  Field,
  FieldGrid,
  OtSelect,
  DraftBar,
  DraftCheckbox,
  SaveTextField,
  errorMessage,
  report,
  reportQuiet,
} from "../common";
import { WhoCard } from "./PreopPanel";
import { NoteEditor } from "./NotesPanel";
import {
  CONSUMPTION_TYPES,
  COUNT_ITEM_TYPES,
  COUNT_JUNCTURES,
  humanise,
} from "../../constants";

/** Whose part of the operation. Order follows who touches the patient
 *  first, not seniority. */
const ROLE_TABS = [
  { id: "nurse", label: "Nurse" },
  { id: "anaesthesia", label: "Anaesthesia" },
  { id: "surgeon", label: "Surgeon" },
];

/**
 * A time field that defaults to now.
 *
 * The in and out buttons used to stamp `now` with no way to say otherwise,
 * which is right only when the button is pressed at the moment it describes --
 * and a theatre is exactly where nobody is free to do that. The theatre charge
 * is computed from these two stamps, so a late press was a wrong bill.
 *
 * Kept as a local ISO string because that is what <input type="datetime-local">
 * speaks; it is converted on the way out.
 */
function TimeAtAction({ label, value, onChange }) {
  return (
    <IpdFormField label={label}>
      <IpdTextField
        fullWidth
        type="datetime-local"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </IpdFormField>
  );
}

/** Now, in the shape <input type="datetime-local"> wants. */
function nowLocal() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

/** A local datetime-local string as a real instant, or undefined. */
function asInstant(local) {
  if (!local) return undefined;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/**
 * Correct the theatre clock after the fact.
 *
 * Separate from the buttons because it answers a different question: not "the
 * patient is going in now" but "the record is wrong". It shows what the times
 * were before the last correction, because a correction nobody can see is
 * indistinguishable from the original reading.
 */
function WheelTimesCard({ otCase, onDone }) {
  const wheel = otCase.wheel_times || {};
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ in_or_at: "", out_of_or_at: "" });
  const [busy, setBusy] = useState(false);

  const toLocal = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  };

  const start = () => {
    setForm({
      in_or_at: toLocal(wheel.in_or_at),
      out_of_or_at: toLocal(wheel.out_of_or_at),
    });
    setOpen(true);
  };

  const minutes =
    wheel.in_or_at && wheel.out_of_or_at
      ? Math.round(
          (new Date(wheel.out_of_or_at) - new Date(wheel.in_or_at)) / 60000,
        )
      : null;

  return (
    <IpdSectionCard
      title="In-room times"
      subtitle="The theatre charge is worked out from these two times."
      actions={
        <Button size="small" onClick={start} disabled={!wheel.in_or_at && !wheel.out_of_or_at}>
          Correct
        </Button>
      }
    >
      <FieldGrid columns={3}>
        <Field label="Patient in room" value={formatIstDateTime(wheel.in_or_at)} />
        <Field label="Patient out of room" value={formatIstDateTime(wheel.out_of_or_at)} />
        <Field
          label="In-room time"
          value={minutes === null ? "—" : `${minutes} min`}
        />
      </FieldGrid>

      {wheel.corrected_at && (
        <Typography sx={{ mt: 1.5, fontSize: 12, color: IPD_COLORS.textSecondary }}>
          Corrected {formatIstDateTime(wheel.corrected_at)} by{" "}
          {wheel.corrected_by_name || "—"}
          {wheel.previous
            ? ` · was ${formatIstDateTime(wheel.previous.in_or_at) || "—"} to ${
                formatIstDateTime(wheel.previous.out_of_or_at) || "—"
              }`
            : ""}
        </Typography>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Correct the in-room times</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary, mb: 2 }}>
            The times the buttons recorded, corrected to what actually happened.
            A charge already raised keeps the figure it was billed on.
          </Typography>
          <Box sx={{ display: "grid", gap: 2 }}>
            <TimeAtAction
              label="Patient in room"
              value={form.in_or_at}
              onChange={(v) => setForm((f) => ({ ...f, in_or_at: v }))}
            />
            <TimeAtAction
              label="Patient out of room"
              value={form.out_of_or_at}
              onChange={(v) => setForm((f) => ({ ...f, out_of_or_at: v }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <IpdButton
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const res = await correctWheelTimes(otCase.id, {
                  in_or_at: asInstant(form.in_or_at),
                  out_of_or_at: asInstant(form.out_of_or_at),
                });
                if (report(res)) {
                  setOpen(false);
                  onDone();
                }
              } finally {
                setBusy(false);
              }
            }}
          >
            Save the correction
          </IpdButton>
        </DialogActions>
      </Dialog>
    </IpdSectionCard>
  );
}

export default function TheatrePanel({ otCase, onChanged }) {
  const [role, setRole] = useState("nurse");
  const [identity, setIdentity] = useState(null);
  const [anaesthesia, setAnaesthesia] = useState(null);
  const [consumption, setConsumption] = useState(null);
  const [counts, setCounts] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [who, setWho] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const [i, a, c, s, r, w] = await Promise.all([
        fetchIdentityCheck(otCase.id),
        fetchAnaesthesiaRecord(otCase.id),
        fetchConsumption(otCase.id),
        fetchSwabCounts(otCase.id),
        fetchReferrals(otCase.id),
        fetchWhoChecklist(otCase.id),
      ]);
      setIdentity(i);
      setAnaesthesia(a);
      setConsumption(c);
      setCounts(s);
      setReferrals(r || []);
      setWho(w);
    } catch (err) {
      setError(errorMessage(err, "Could not load the theatre record"));
    }
  }, [otCase.id]);

  useEffect(() => {
    load();
  }, [load]);

  // Three tiers, because these are three different costs.
  //
  //   patch      — the save already returned the new row. Zero requests.
  //                Everything ticked repeatedly lives here: the identity
  //                check, the WHO sections, the pre-induction checklist.
  //   reloadOne  — the payload carries a derived figure the save does not
  //                (an unbilled count, a check-out verdict), so one endpoint
  //                is re-read. One request.
  //   refreshAll — the CASE moved. Everything is re-read and the parent is
  //                told. Two actions on this panel do that.
  //
  // All of it used to be refreshAll: seven GETs and a case reload for every
  // checkbox on a card that carries seventeen of them.
  const refreshAll = async () => {
    await load();
    onChanged();
  };

  const reloadAnaesthesia = async () => setAnaesthesia(await fetchAnaesthesiaRecord(otCase.id));
  const reloadConsumption = async () => setConsumption(await fetchConsumption(otCase.id));
  const reloadCounts = async () => setCounts(await fetchSwabCounts(otCase.id));
  const reloadReferrals = async () => setReferrals((await fetchReferrals(otCase.id)) || []);

  if (error) return <Typography sx={{ color: IPD_COLORS.error.text }}>{error}</Typography>;

  // Three people work in this room and each has a different job. One long
  // scroll of eight cards made everybody read past everybody else's work;
  // three short ones do not. Organisational, not restrictive — the tabs
  // are all reachable, because a theatre covers for itself and there is no
  // separate anaesthetist login to key off anyway (ADR-0007).
  //
  // Each tab carries its own note, which is where they belong: the person
  // who did the work writes it up beside the record of the work. The Notes
  // tab keeps the merged record, which is a different document.
  const panels = {
    nurse: (
      <Box sx={{ display: "grid", gap: 2.5 }}>
        <IdentityCard
          otCase={otCase}
          identity={identity}
          onPatch={(row) => setIdentity((c) => ({ ...c, identity_check: row }))}
          onEntered={refreshAll}
        />
        <WheelTimesCard otCase={otCase} onDone={onChanged} />
        <CountsCard otCase={otCase} counts={counts} onDone={reloadCounts} />
        <ConsumptionCard
          otCase={otCase}
          consumption={consumption}
          onDone={reloadConsumption}
        />
        <WhoCard
          otCase={otCase}
          who={who}
          sections={["SIGN_OUT"]}
          onPatch={setWho}
        />
        <NoteEditor otCase={otCase} kind="nursing" onChanged={onChanged} />
        <CheckoutCard otCase={otCase} counts={counts} onDone={refreshAll} />
      </Box>
    ),
    anaesthesia: (
      <Box sx={{ display: "grid", gap: 2.5 }}>
        <AnaesthesiaCard
          otCase={otCase}
          record={anaesthesia}
          onPatch={(row) => setAnaesthesia((c) => ({ ...c, record: row }))}
          onReload={reloadAnaesthesia}
        />
        <NoteEditor otCase={otCase} kind="anaesthesia" onChanged={onChanged} />
      </Box>
    ),
    surgeon: (
      <Box sx={{ display: "grid", gap: 2.5 }}>
        <WhoCard
          otCase={otCase}
          who={who}
          sections={["TIME_OUT"]}
          onPatch={setWho}
        />
        <ReferralCard otCase={otCase} referrals={referrals} onDone={reloadReferrals} />
        <NoteEditor otCase={otCase} kind="surgical" onChanged={onChanged} />
      </Box>
    ),
  };

  return (
    <Box>
      <IpdTabBar
        tabs={ROLE_TABS}
        activeTab={role}
        onChange={setRole}
        ariaLabel="Whose part of the operation"
      />
      {panels[role]}
    </Box>
  );
}

function IdentityCard({ otCase, identity, onPatch, onEntered }) {
  const row = identity?.identity_check;
  const expected = identity?.expected || {};

  // The door check is answered as one conversation with the patient, not as
  // seven separate decisions, so it is written back once.
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const save = async (values, extra) => {
    const merged = { ...draft, ...values };
    setSaving(true);
    const res = await saveIdentityCheck(otCase.id, { values: merged, ...extra });
    setSaving(false);
    if (!reportQuiet(res)) return false;
    onPatch(res.data);
    setDraft({});
    return true;
  };

  const tick = (item, next) => setDraft((d) => ({ ...d, [item]: next }));

  // The server's answer for an item, unless a pending tick overrides it. The
  // instrument-sets flag is a column rather than a checklist value, so it is
  // read from its own field and drafted under its own name.
  const serverValue = (item) =>
    item === "instrument_sets_available"
      ? row?.instrument_sets_available
      : row?.values?.[item];
  const shown = (item) => draft[item] ?? serverValue(item);
  const unsaved = Object.keys(draft).filter(
    (i) => Boolean(draft[i]) !== Boolean(serverValue(i))
  ).length;

  return (
    <IpdSectionCard
      title="Identity and operation check"
      subtitle="The right patient, for the right operation, at the theatre door."
      actions={
        <Button
          size="small"
          variant="contained"
          disabled={otCase.lifecycle_status === "IN_OR" || !row?.completed}
          onClick={async () => {
            // The time is sent explicitly rather than left to default to the
            // moment the button was pressed -- which in a theatre is often
            // some minutes after the patient actually went through the door,
            // and the charge is computed from it.
            if (report(await enterTheatre(otCase.id))) onEntered();
          }}
        >
          {otCase.lifecycle_status === "IN_OR" ? "In theatre" : "Patient enters theatre"}
        </Button>
      }
    >
      <FieldGrid columns={4}>
        <Field label="Expected patient" value={expected.patient_name} />
        <Field label="Expected procedure" value={expected.procedure_name} />
        <Field label="Expected theatre" value={expected.operating_room_name} />
        <Field label="In theatre at" value={formatIstDateTime(otCase.in_or_at)} />
      </FieldGrid>


      {!row ? (
        <Button sx={{ mt: 2 }} size="small" variant="outlined" onClick={() => save({})}>
          Start the check
        </Button>
      ) : (
        <>
          <Box sx={{ mt: 2 }}>
            <CheckList
              done={
                row.items.filter((i) => shown(i)).length +
                (shown("instrument_sets_available") ? 1 : 0)
              }
              total={row.items.length + 1}
              blocking="all of these before the patient enters"
            >
              {row.items.map((item) => (
                <DraftCheckbox
                  key={item}
                  checked={shown(item)}
                  disabled={saving}
                  label={humanise(item.replace(/_verified$/, ""))}
                  onChange={(next) => tick(item, next)}
                />
              ))}
              <DraftCheckbox
                checked={shown("instrument_sets_available")}
                disabled={saving}
                label="Instrument sets and drapes ready"
                onChange={(next) => tick("instrument_sets_available", next)}
              />
            </CheckList>
            <DraftBar
              count={unsaved}
              busy={saving}
              onSave={() => save({})}
              onDiscard={() => setDraft({})}
            />
            </Box>

            <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <IpdFormField label="Surgical site">
                <SaveTextField
                  value={row.surgical_site || ""}
                  onSave={(v) => save({}, { surgical_site: v })}
                />
              </IpdFormField>
              <IpdFormField label="Side">
                <SaveTextField
                  value={row.surgical_side || ""}
                  onSave={(v) => save({}, { surgical_side: v })}
                />
              </IpdFormField>
            </Box>
            <Typography sx={{ mt: 1.5, fontSize: 12.5, fontWeight: 600 }}>
              {row.completed
                ? `Verified by ${row.verified_by_name || "—"} at ${formatIstDateTime(row.completed_at)}`
                : "Not complete — the patient cannot enter theatre yet."}
            </Typography>
          </>
        )}
    </IpdSectionCard>
  );
}

function AnaesthesiaCard({ otCase, record, onPatch, onReload }) {
  const [drugOpen, setDrugOpen] = useState(false);
  const [monitorOpen, setMonitorOpen] = useState(false);
  const row = record?.record;

  // The pre-induction list is worked through at the machine in one pass.
  const [gaDraft, setGaDraft] = useState({});
  const [savingGa, setSavingGa] = useState(false);

  const save = async (payload) => {
    const res = await saveAnaesthesiaRecord(otCase.id, payload);
    if (!reportQuiet(res)) return false;
    onPatch(res.data);
    return true;
  };

  const saveGa = async () => {
    setSavingGa(true);
    const ok = await save({ ga_checklist: gaDraft });
    setSavingGa(false);
    if (ok) setGaDraft({});
  };

  return (
    <IpdSectionCard
      title="Anaesthesia record"
      subtitle="The live record. The summary belongs in the anaesthesia note."
      actions={
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" disabled={!row} onClick={() => setDrugOpen(true)}>
            Add drug
          </Button>
          <Button size="small" disabled={!row} onClick={() => setMonitorOpen(true)}>
            Add observation
          </Button>
        </Box>
      }
    >
      {!row ? (
        <Button size="small" variant="outlined" onClick={() => save({})}>
          Start the anaesthesia record
        </Button>
      ) : (
        <>
          <CheckList
            title="PRE-INDUCTION CHECKLIST"
            done={
              row.ga_checklist_items.filter(
                (i) => gaDraft[i] ?? row.ga_checklist[i]
              ).length
            }
            total={row.ga_checklist_items.length}
          >
            {row.ga_checklist_items.map((item) => (
              <DraftCheckbox
                key={item}
                checked={gaDraft[item] ?? row.ga_checklist[item]}
                disabled={savingGa}
                label={humanise(item.replace(/^ga_/, ""))}
                onChange={(next) => setGaDraft((d) => ({ ...d, [item]: next }))}
              />
            ))}
          </CheckList>
          <DraftBar
            count={
              Object.keys(gaDraft).filter(
                (i) => Boolean(gaDraft[i]) !== Boolean(row.ga_checklist[i])
              ).length
            }
            busy={savingGa}
            onSave={saveGa}
            onDiscard={() => setGaDraft({})}
          />

          <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
            <IpdFormField label="Technique">
              <IpdTextField
                fullWidth
                defaultValue={row.technique || ""}
                onBlur={(e) => save({ technique: e.target.value })}
              />
            </IpdFormField>
            <IpdFormField label="Airway management">
              <IpdTextField
                fullWidth
                defaultValue={row.airway_management || ""}
                onBlur={(e) => save({ airway_management: e.target.value })}
              />
            </IpdFormField>
            {["pre_anaesthesia_findings", "intra_anaesthesia_findings", "post_anaesthesia_findings"].map(
              (key) => (
                <IpdFormField key={key} label={humanise(key)}>
                  <IpdTextField
                    fullWidth
                    multiline
                    minRows={2}
                    defaultValue={row[key] || ""}
                    onBlur={(e) => save({ [key]: e.target.value })}
                  />
                </IpdFormField>
              )
            )}
          </Box>

          <Box sx={{ mt: 2.5 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: 1 }}>
              Drugs given ({(record?.drugs || []).length})
            </Typography>
            {(record?.drugs || []).map((d) => (
              <Typography key={d.id} sx={{ fontSize: 13, mb: 0.4 }}>
                {formatIstTime(d.given_at)} — {d.drug_name} {d.dose || ""} {d.route || ""} ({d.phase})
              </Typography>
            ))}
          </Box>

          <Box sx={{ mt: 2.5 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: 1 }}>
              Monitoring ({(record?.monitoring || []).length})
            </Typography>
            {(record?.monitoring || []).map((m) => (
              <Typography key={m.id} sx={{ fontSize: 13, mb: 0.4 }}>
                {formatIstTime(m.recorded_at)} — BP {m.systolic_bp ?? "—"}/{m.diastolic_bp ?? "—"}, HR{" "}
                {m.heart_rate ?? "—"}, SpO₂ {m.spo2 ?? "—"}, EtCO₂ {m.etco2 ?? "—"}
                {m.event_note ? ` · ${m.event_note}` : ""}
              </Typography>
            ))}
          </Box>
        </>
      )}

      <SimpleDialog
        open={drugOpen}
        title="Record a drug given"
        fields={[
          { key: "drug_name", label: "Drug", required: true },
          { key: "dose", label: "Dose" },
          { key: "route", label: "Route" },
          {
            key: "phase",
            label: "Phase",
            select: [
              { value: "PRE", label: "Pre" },
              { value: "INDUCTION", label: "Induction" },
              { value: "INTRA", label: "Intra" },
              { value: "POST", label: "Post" },
            ],
            initial: "INTRA",
          },
          {
            key: "is_prophylactic_antibiotic",
            label: "This is the prophylactic antibiotic",
            checkbox: true,
            hint:
              "Ticking this is what makes the 60-minutes-before-incision measure " +
              "computable — the time it was given and the incision time are already " +
              "recorded, but nothing else says which drug was the dose.",
          },
        ]}
        onClose={() => setDrugOpen(false)}
        onSubmit={async (values) => {
          if (report(await addAnaesthesiaDrug(otCase.id, values))) {
            setDrugOpen(false);
            onReload();
          }
        }}
      />

      <SimpleDialog
        open={monitorOpen}
        title="Record an interval observation"
        fields={[
          { key: "systolic_bp", label: "Systolic BP" },
          { key: "diastolic_bp", label: "Diastolic BP" },
          { key: "heart_rate", label: "Heart rate" },
          { key: "spo2", label: "SpO₂" },
          { key: "etco2", label: "EtCO₂" },
          { key: "fio2", label: "FiO₂ (%)" },
          { key: "ecg_rhythm", label: "ECG rhythm" },
          { key: "temperature", label: "Temperature" },
          { key: "event_note", label: "What was happening" },
        ]}
        onClose={() => setMonitorOpen(false)}
        onSubmit={async (values) => {
          if (report(await addAnaesthesiaMonitor(otCase.id, values))) {
            setMonitorOpen(false);
            onReload();
          }
        }}
      />
    </IpdSectionCard>
  );
}


/**
 * Pick a consumable from the inventory catalogue.
 *
 * The item name and price used to be typed, every case, from memory — which is
 * why the same suture pack could be £120 on one case and £180 on the next, and
 * why nothing could be reconciled against stock. Choosing a real item brings
 * its code, unit and price with it, and records the inventory id on the
 * consumption row.
 *
 * Typing something the catalogue does not have still works: a theatre cannot
 * stop to raise a master-data ticket mid-case. What is lost is the link, not
 * the record.
 */
function ItemPicker({ value, onPick, onType }) {
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const q = (value || "").trim();
    if (q.length < 2) {
      setMatches([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        setMatches(await searchInventoryItems(q));
      } catch (err) {
        // The catalogue being unreachable must not stop the theatre recording
        // what it used. The field stays a plain text box.
        setMatches([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [value]);

  return (
    <Box sx={{ position: "relative" }}>
      <IpdTextField
        fullWidth
        placeholder="start typing to search the catalogue"
        value={value || ""}
        onChange={(e) => onType(e.target.value)}
      />
      {searching && (
        <Typography sx={{ fontSize: 11, color: IPD_COLORS.textMuted, mt: 0.25 }}>
          searching…
        </Typography>
      )}
      {matches.length > 0 && (
        <Box
          sx={{
            position: "absolute",
            zIndex: 6,
            left: 0,
            right: 0,
            mt: 0.5,
            maxHeight: 220,
            overflowY: "auto",
            bgcolor: IPD_COLORS.cardBg,
            border: `1px solid ${IPD_COLORS.border}`,
            borderRadius: `${IPD_SHAPE.radiusMd}px`,
            boxShadow: IPD_COLORS.shadow.menu,
          }}
        >
          {matches.map((item) => {
            const price = itemPriceOf(item);
            return (
              <Box
                key={item.item_id || item.id || item.item_code}
                onClick={() => {
                  onPick(item);
                  setMatches([]);
                }}
                sx={{
                  px: 1.5,
                  py: 1,
                  cursor: "pointer",
                  display: "flex",
                  gap: 1,
                  alignItems: "baseline",
                  "&:hover": { bgcolor: IPD_COLORS.pageBg },
                }}
              >
                <Typography sx={{ fontSize: 13, flex: 1 }}>{item.item_name}</Typography>
                <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textMuted }}>
                  {item.base_uom || item.uom_name || ""}
                </Typography>
                {price && (
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{price}</Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}


function ConsumptionCard({ otCase, consumption, onDone }) {
  const [open, setOpen] = useState(false);
  const items = consumption?.items || [];

  return (
    <IpdSectionCard
      title="What was used"
      subtitle={
        consumption?.billable
          ? "Items are billed against the case's admission."
          : "This case has no admission, so items are recorded but not billed."
      }
      actions={
        <Button size="small" variant="contained" onClick={() => setOpen(true)}>
          Record an item
        </Button>
      }
    >
      {items.length === 0 ? (
        <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
          Nothing recorded yet.
        </Typography>
      ) : (
        items.map((c) => (
          <Box
            key={c.id}
            sx={{ py: 1, borderBottom: `1px solid ${IPD_COLORS.border}` }}
          >
            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
              {c.item_name} × {c.quantity} {c.unit || ""}
            </Typography>
            <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
              {c.item_type}
              {c.batch_no ? ` · batch ${c.batch_no}` : ""}
              {c.serial_no ? ` · serial ${c.serial_no}` : ""}
              {c.device_identifier ? ` · DI ${c.device_identifier}` : ""}
              {c.unit_price ? ` · ${c.unit_price}` : ""}
              {c.billed ? " · billed" : " · not billed"}
              {c.billing_error ? ` · ${c.billing_error}` : ""}
            </Typography>
        </Box>
        ))
      )}

      <SimpleDialog
        open={open}
        title="Record an item used"
        fields={[
          { key: "item_type", label: "Type", select: CONSUMPTION_TYPES, initial: "SUTURE" },
          {
            key: "item_name",
            label: "Item",
            required: true,
            picker: true,
            hint:
              "Picking from the catalogue fills the unit and the price, and links the row to inventory. Typing a name the catalogue does not have still works.",
          },
          { key: "quantity", label: "Quantity", initial: "1" },
          { key: "unit", label: "Unit" },
          { key: "unit_price", label: "Unit price" },
          { key: "batch_no", label: "Batch" },
          { key: "serial_no", label: "Serial (implants)" },
          {
            key: "udi_raw",
            label: "Scan the UDI",
            multiline: true,
            placeholder: "(01)00819320041059(17)250630(10)LOT42",
            hint:
              "Scanning fills in the batch, serial and expiry, and records the device " +
              "identifier — which is what a recall is issued against. Anything you have " +
              "typed above wins over the scan.",
          },
        ]}
        onClose={() => setOpen(false)}
        onSubmit={async (values) => {
          if (report(await recordConsumption(otCase.id, values))) {
            setOpen(false);
            onDone();
          }
        }}
      />
    </IpdSectionCard>
  );
}

/**
 * Record one counting juncture (AORN).
 *
 * The number means different things at different moments, and the form says
 * which it wants rather than leaving the nurse to work it out: at "items added"
 * it is how many were opened onto the field, and everywhere else it is the total
 * on the field now. Getting that the wrong way round is the easiest mistake to
 * make at a keyboard mid-case.
 */
function CountJunctureDialog({ row, onClose, onDone }) {
  const [form, setForm] = useState({
    juncture: "SKIN_CLOSURE",
    counted: "",
    witnessed_by_name: "",
  });

  useEffect(() => {
    if (row) setForm({ juncture: "SKIN_CLOSURE", counted: "", witnessed_by_name: "" });
  }, [row]);

  const chosen = COUNT_JUNCTURES.find((j) => j.value === form.juncture);

  return (
    <Dialog open={Boolean(row)} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{row ? `Count \u2014 ${row.item_name}` : "Count"}</DialogTitle>
      <DialogContent>
        <IpdFormField label="Juncture">
          <OtSelect
            value={form.juncture}
            onChange={(e) => setForm((f) => ({ ...f, juncture: e.target.value }))}
            options={COUNT_JUNCTURES}
          />
        </IpdFormField>
        <Box sx={{ mt: 2 }}>
          <IpdFormField label={chosen?.hint || "Counted"}>
            <IpdTextField
              fullWidth
              value={form.counted}
              onChange={(e) => setForm((f) => ({ ...f, counted: e.target.value }))}
            />
          </IpdFormField>
        </Box>
        <Box sx={{ mt: 2 }}>
          <IpdFormField label="Witnessed by">
            <IpdTextField
              fullWidth
              placeholder="The second person of the two-person check"
              value={form.witnessed_by_name}
              onChange={(e) => setForm((f) => ({ ...f, witnessed_by_name: e.target.value }))}
            />
          </IpdFormField>
        </Box>
        {row && form.juncture !== "ADDED" && (
          <Typography sx={{ mt: 1.5, fontSize: 12.5, color: IPD_COLORS.textSecondary }}>
            Expected {row.expected_at_closure} &mdash; {row.initial_count ?? 0} opened at the start
            plus {row.added_count} added since.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <IpdButton
          disabled={String(form.counted).trim() === ""}
          onClick={async () => {
            if (report(await recordCountJuncture(row.id, form))) onDone();
          }}
        >
          Record
        </IpdButton>
      </DialogActions>
    </Dialog>
  );
}


function CountsCard({ otCase, counts, onDone }) {
  const [open, setOpen] = useState(false);
  const [verifying, setVerifying] = useState(null);
  const [counting, setCounting] = useState(null);
  const rows = counts?.counts || [];

  return (
    <IpdSectionCard
      title="Swab and instrument count"
      subtitle="Counted at each juncture. A mismatch blocks check-out."
      actions={
        <Button size="small" variant="contained" onClick={() => setOpen(true)}>
          Start a count
        </Button>
      }
    >
      {/* The server's own blockers, verbatim. A screen that summarises them
          in its own words is a second opinion, and the two drift. */}
      {!counts?.may_check_out && (counts?.checkout_blockers || []).length > 0 && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 1,
            backgroundColor: IPD_COLORS.error.bg,
            color: IPD_COLORS.error.text,
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          {counts.checkout_blockers.map((blocker) => (
            <Box key={blocker} sx={{ mb: 0.5 }}>
              {blocker}
            </Box>
          ))}
          </Box>
        )}
        {rows.length === 0 ? (
          <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
            No count started.
          </Typography>
        ) : (
          rows.map((c) => (
            <Box
              key={c.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                py: 1.2,
                borderBottom: `1px solid ${IPD_COLORS.border}`,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 180 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                  {c.item_name} <span style={{ fontWeight: 400 }}>({c.item_type})</span>
                </Typography>
                <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                  initial {c.initial_count ?? "—"} + added {c.added_count} = expected{" "}
                  {c.expected_at_closure} · closure {c.closure_count ?? "not taken"}
                  {c.resolved ? " · resolved" : ""}
                  {c.discrepancy_reason ? ` · ${c.discrepancy_reason}` : ""}
                </Typography>
              </Box>
              <CountResultChip result={c.result} />
              <IpdTextField
                size="small"
                sx={{ width: 110 }}
                placeholder="closure"
                defaultValue={c.closure_count ?? ""}
                onBlur={async (e) => {
                  if (e.target.value === "") return;
                  if (report(await updateSwabCount(c.id, { closure_count: e.target.value, circulating_sign: true })))
                    onDone();
                }}
              />
              <Button size="small" variant="outlined" onClick={() => setCounting(c)}>
                Count
              </Button>
              {c.result === "DISCREPANCY" && !c.resolved && (
                <Button size="small" color="error" onClick={() => setVerifying(c)}>
                  Verify
                </Button>
              )}
              {/* The sequence, oldest first. Three numbers on a row say what
                  the count came to; this says when it was taken and by whom,
                  which is what the standard is actually about. */}
              {(c.events || []).length > 0 && (
                <Box sx={{ width: "100%", pl: 0.5 }}>
                  {c.events.map((e) => (
                    <Typography
                      key={e.id}
                      sx={{
                        fontSize: 11.5,
                        color: e.matched === false ? IPD_COLORS.error.text : IPD_COLORS.textSecondary,
                        fontWeight: e.matched === false ? 700 : 400,
                      }}
                    >
                      {formatIstDateTime(e.counted_at)} · {e.juncture_label} · counted{" "}
                      {e.counted}
                      {e.expected === null || e.expected === undefined
                        ? ""
                        : ` of ${e.expected}`}
                      {e.counted_by_name ? ` · ${e.counted_by_name}` : ""}
                      {e.witnessed_by_name ? ` with ${e.witnessed_by_name}` : ""}
                    </Typography>
                  ))}
                </Box>
              )}
          </Box>
        ))
      )}

      <SimpleDialog
        open={open}
        title="Start counting an item"
        fields={[
          { key: "item_type", label: "Type", select: COUNT_ITEM_TYPES, initial: "SWAB" },
          { key: "item_name", label: "Item", required: true },
          { key: "initial_count", label: "Initial count", required: true },
          { key: "added_count", label: "Added during the case", initial: "0" },
        ]}
        onClose={() => setOpen(false)}
        onSubmit={async (values) => {
          if (report(await startSwabCount(otCase.id, values))) {
            setOpen(false);
            onDone();
          }
        }}
      />

      <VerifyDiscrepancyDialog
        row={verifying}
        onClose={() => setVerifying(null)}
        onDone={() => {
          setVerifying(null);
          onDone();
        }}
      />

      <CountJunctureDialog
        row={counting}
        onClose={() => setCounting(null)}
        onDone={() => {
          setCounting(null);
          onDone();
        }}
      />
    </IpdSectionCard>
  );
}

function VerifyDiscrepancyDialog({ row, onClose, onDone }) {
  const [form, setForm] = useState({
    discrepancy_reason: "",
    surgeon_informed: false,
    senior_verified: false,
  });

  useEffect(() => {
    setForm({ discrepancy_reason: "", surgeon_informed: false, senior_verified: false });
  }, [row]);

  if (!row) return null;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Resolve the count discrepancy — {row.item_name}</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mb: 2 }}>
          Expected {row.expected_at_closure}, counted {row.closure_count}. All three are required
          together: a documented reason, the surgeon informed, and a senior verification.
        </Typography>
        <IpdFormField label="Why did the count not match?">
          <IpdTextField
            fullWidth
            multiline
            minRows={2}
            autoFocus
            value={form.discrepancy_reason}
            onChange={(e) => setForm((f) => ({ ...f, discrepancy_reason: e.target.value }))}
          />
        </IpdFormField>
        <FormControlLabel
          sx={{ mt: 1, display: "block" }}
          control={
            <Checkbox
              checked={form.surgeon_informed}
              onChange={(e) => setForm((f) => ({ ...f, surgeon_informed: e.target.checked }))}
            />
          }
          label={<Typography sx={{ fontSize: 13 }}>The surgeon has been informed</Typography>}
        />
        <FormControlLabel
          sx={{ display: "block" }}
          control={
            <Checkbox
              checked={form.senior_verified}
              onChange={(e) => setForm((f) => ({ ...f, senior_verified: e.target.checked }))}
            />
          }
          label={
            <Typography sx={{ fontSize: 13 }}>
              A senior officer has verified the finding
            </Typography>
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <IpdButton
          disabled={!form.discrepancy_reason || !form.surgeon_informed || !form.senior_verified}
          onClick={async () => {
            if (report(await verifySwabCount(row.id, form))) onDone();
          }}
        >
          Resolve
        </IpdButton>
      </DialogActions>
    </Dialog>
  );
}

function ReferralCard({ otCase, referrals, onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <IpdSectionCard
      title="Interdepartmental referral"
      subtitle="For an unexpected finding that needs another discipline's opinion."
      actions={
        <Button size="small" variant="contained" onClick={() => setOpen(true)}>
          Raise a referral
        </Button>
      }
    >
      {referrals.length === 0 ? (
        <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 13.5 }}>
          None raised.
        </Typography>
      ) : (
        referrals.map((r) => (
          <Box key={r.id} sx={{ py: 1, borderBottom: `1px solid ${IPD_COLORS.border}` }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
              {r.to_department_name || r.to_doctor_name} · {r.urgency}
            </Typography>
            <Typography sx={{ fontSize: 12.5 }}>{r.reason}</Typography>
            <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
              {r.acknowledged
                ? `Acknowledged by ${r.acknowledged_by_name} — ${r.response || "no response text"}`
                : "Not yet acknowledged"}
            </Typography>
          </Box>
        ))
      )}
      <SimpleDialog
        open={open}
        title="Raise a referral"
        fields={[
          { key: "to_doctor_name", label: "Doctor" },
          { key: "to_department_name", label: "Department" },
          {
            key: "urgency",
            label: "Urgency",
            select: [
              { value: "ROUTINE", label: "Routine" },
              { value: "URGENT", label: "Urgent" },
              { value: "IMMEDIATE", label: "Immediate" },
            ],
            initial: "ROUTINE",
          },
          { key: "reason", label: "What is the referral for", required: true, multiline: true },
        ]}
        onClose={() => setOpen(false)}
        onSubmit={async (values) => {
          if (report(await raiseReferral(otCase.id, values))) {
            setOpen(false);
            onDone();
          }
        }}
      />
    </IpdSectionCard>
  );
}

function CheckoutCard({ otCase, counts, onDone }) {
  const [note, setNote] = useState("");
  const [outAt, setOutAt] = useState(nowLocal);
  const [certified, setCertified] = useState(false);
  const blocked = counts && !counts.may_check_out;

  return (
    <IpdSectionCard
      title="Check out of theatre"
      subtitle="The anaesthetist certifies the patient fit to move to recovery."
    >
      {otCase.fit_certified ? (
        <FieldGrid columns={3}>
          <Field label="Certified by" value={otCase.fit_certified_by_name} />
          <Field label="Left theatre" value={formatIstDateTime(otCase.out_of_or_at)} />
          <Field label="Note" value={otCase.ot_checkout_note} />
        </FieldGrid>
      ) : (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={certified}
                onChange={(e) => setCertified(e.target.checked)}
              />
            }
            label={
              <Typography sx={{ fontSize: 13.5 }}>
                I certify the patient is fit to be transferred to recovery
              </Typography>
            }
          />
          <Box sx={{ mt: 1, maxWidth: 260 }}>
            {/* Same reason as the wheel-in time: the charge is the interval
                between these two stamps. */}
            <TimeAtAction label="Patient left theatre at" value={outAt} onChange={setOutAt} />
          </Box>
          <Box sx={{ mt: 1 }}>
            <IpdFormField label="Note">
              <IpdTextField fullWidth value={note} onChange={(e) => setNote(e.target.value)} />
            </IpdFormField>
          </Box>
          <Box sx={{ mt: 2 }}>
            <IpdButton
              disabled={!certified || blocked || otCase.lifecycle_status !== "IN_OR"}
              onClick={async () => {
                if (
                  report(
                    await otCheckout(otCase.id, {
                      fit_certified: true,
                      note,
                      out_of_or_at: asInstant(outAt),
                    }),
                  )
                )
                  onDone();
              }}
            >
              Check out of theatre
            </IpdButton>
            {blocked &&
              (counts.checkout_blockers || []).map((blocker) => (
                <Typography
                  key={blocker}
                  sx={{ mt: 1, fontSize: 12.5, color: IPD_COLORS.error.text }}
                >
                  {blocker}
                </Typography>
              ))}
            {/* Not a blocker, on purpose: holding a patient in theatre until
                the operation note is typed would be answered by typing
                nothing useful. Said here so it is not discovered at the
                audit instead. */}
            {(counts?.checkout_warnings || []).map((warning) => (
              <Typography
                key={warning}
                sx={{ mt: 1, fontSize: 12.5, color: IPD_COLORS.warning.text }}
              >
                {warning}
              </Typography>
            ))}
          </Box>
        </>
      )}
    </IpdSectionCard>
  );
}

/**
 * A dialog over a flat list of fields. Six forms in this panel are the same
 * shape — a handful of inputs and a submit — so they share one component rather
 * than being six near-identical dialogs.
 */
export function SimpleDialog({ open, title, fields, onClose, onSubmit }) {
  const initial = () =>
    fields.reduce((acc, f) => ({ ...acc, [f.key]: f.initial ?? "" }), {});
  const [values, setValues] = useState(initial);

  useEffect(() => {
    if (open) setValues(initial());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const missing = fields.some(
    (f) => f.required && !f.checkbox && !String(values[f.key] || "").trim()
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mt: 0.5 }}>
          {fields.filter((f) => !f.hidden).map((f) => (
            <Box key={f.key} sx={{ gridColumn: f.multiline || f.checkbox ? "span 2" : "span 1" }}>
              {f.picker ? (
                <IpdFormField label={f.label}>
                  <ItemPicker
                    value={values[f.key]}
                    onType={(text) => setValues((v) => ({ ...v, [f.key]: text }))}
                    onPick={(item) =>
                      setValues((v) => ({
                        ...v,
                        [f.key]: item.item_name,
                        // Everything the catalogue knows, so nobody
                        // retypes it and nothing drifts.
                        inventory_item_id: item.item_id || item.id || "",
                        item_code: item.item_code || "",
                        unit: item.base_uom || item.uom_name || v.unit || "",
                        unit_price: itemPriceOf(item) || v.unit_price || "",
                      }))
                    }
                  />
                </IpdFormField>
              ) : f.checkbox ? (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={Boolean(values[f.key])}
                      onChange={(e) =>
                        setValues((v) => ({ ...v, [f.key]: e.target.checked }))
                      }
                    />
                  }
                  label={<Typography sx={{ fontSize: 13 }}>{f.label}</Typography>}
                />
              ) : (
                <IpdFormField label={f.label}>
                  {f.select ? (
                    <OtSelect
                      value={values[f.key] ?? ""}
                      onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                      options={f.select}
                    />
                  ) : (
                    <IpdTextField
                      fullWidth
                      multiline={Boolean(f.multiline)}
                      minRows={f.multiline ? 2 : undefined}
                      placeholder={f.placeholder}
                      value={values[f.key] ?? ""}
                      onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                    />
                  )}
                </IpdFormField>
              )}
              {f.hint && (
                <Typography sx={{ mt: 0.5, fontSize: 11.5, color: IPD_COLORS.textSecondary }}>
                  {f.hint}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <IpdButton disabled={missing} onClick={() => onSubmit(values)}>
          Save
        </IpdButton>
      </DialogActions>
    </Dialog>
  );
}
